import Head from 'next/head';
import type { GetStaticPaths, GetStaticProps } from 'next';

import Layout from '@/components/layout';
import SiteContextProvider from '@/contexts/siteContext';
import { DEFAULT_REVALIDATE_TIME } from '@/constants';
import { getEventsBySite, getSingleSiteData } from '@/lib/fetchers/sites';
import { getSiteVenue } from '@/lib/utils';
import type { Event, SitePageProps, SiteParams } from '@/types';

interface HistoryProps extends SitePageProps {
  events: Array<Event>;
}

export const getStaticPaths: GetStaticPaths<SiteParams> = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<HistoryProps, SiteParams> = async ({
  params,
}) => {
  const [site, events] = await Promise.all([
    getSingleSiteData(params?.site),
    getEventsBySite(params?.site, { type: 'past' }),
  ]);

  // Display a 404 if there are no past events
  if (!events || (Array.isArray(events) && events.length === 0)) {
    return {
      notFound: true,
    };
  }

  return {
    props: { events, site },
    revalidate: Number.parseInt(
      process.env.ISR_REVALIDATE_TIME || DEFAULT_REVALIDATE_TIME,
    ),
  };
};

const History = ({ events, site }: HistoryProps) => {
  const venue = getSiteVenue(site);

  return (
    <SiteContextProvider site={site}>
      <Head>
        <title key="title">History</title>
        <meta
          name="description"
          content={`Past events at ${venue.name}`}
          key="meta:description"
        />
      </Head>
      <Layout>
        <h1 className="border-bottom border-gray mb-5 pb-3">Past Events</h1>
        <div className="row">
          {events.map((event) => (
            <div className="col-md-4 text-center" key={event.id}>
              <div className="mb-4">
                <a
                  href={`/e/${event.id}`}
                  className="text-decoration-none d-block"
                >
                  {event.url_image ? (
                    <img
                      className="img-fluid img-greyscale"
                      src={event.url_image}
                      alt={`${event.name} event flyer`}
                      loading="lazy"
                    />
                  ) : (
                    <>{event.name}</>
                  )}
                </a>
              </div>
            </div>
          ))}
        </div>
      </Layout>
    </SiteContextProvider>
  );
};

export default History;
